/**
 * customerAuth.js — the ONE source of truth for customer authentication tokens
 * across the entire DoozyGo customer surface. Replaces the previous split-brain
 * where:
 *   • CustomerPortal.js / DoozyGoMagic.js used sessionStorage.cs_tok (base64)
 *   • OnlineBookingSection.js / LeadCaptureCard.js used localStorage.customer_session (raw)
 *
 * That mismatch caused:
 *   – "Please login" prompts in Booking even though CustomerPortal said I was logged in
 *   – Address + name fields silently resetting when the user switched tabs
 *   – Lost cart items after the OAuth redirect chain
 *
 * This helper exposes a tiny API every page uses:
 *   • getCustomerToken() -> string | ""   — read raw token
 *   • setCustomerToken(token)             — persist token everywhere needed
 *   • clearCustomerToken()                — logout
 *   • getCustomerAuthHeaders()            — ready-to-spread Authorization headers
 *   • isCustomerLoggedIn()                — synchronous boolean
 *
 * Storage strategy:
 *   • Primary: localStorage['customer_session']  (survives tab close & restart)
 *   • Mirrored: sessionStorage['cs_tok'] (base64)  — kept for backward compat
 *     with any legacy inline <script> that reads it pre-React (CustomerPortal.js
 *     references a pre-React script).
 *   • Legacy drain: on first read, if we find only the legacy base64 session key,
 *     we decode and promote it to the primary. No logouts, no data loss.
 */

const PRIMARY_KEY = "customer_session";
const LEGACY_SESSION_KEY = "cs_tok"; // sessionStorage, base64-encoded (CustomerPortal.js, DoozyGoMagic.js)

const safeLocal = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* storage disabled */ } },
  rm(k) { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};
const safeSession = {
  get(k) { try { return sessionStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } },
  rm(k) { try { sessionStorage.removeItem(k); } catch { /* ignore */ } },
};

/** Read the raw bearer token from the unified store (migrates legacy on first read). */
export function getCustomerToken() {
  let token = safeLocal.get(PRIMARY_KEY) || "";
  if (!token) {
    // Legacy drain: decode base64 cs_tok from sessionStorage and promote it.
    const encoded = safeSession.get(LEGACY_SESSION_KEY);
    if (encoded) {
      try {
        token = atob(encoded);
        if (token) safeLocal.set(PRIMARY_KEY, token);
      } catch { token = ""; }
    }
  }
  return token;
}

/** Persist token everywhere so every page sees the same login state. */
export function setCustomerToken(token) {
  if (!token) return;
  safeLocal.set(PRIMARY_KEY, token);
  // Keep legacy mirror so pre-React inline bootstrap scripts still work.
  try { safeSession.set(LEGACY_SESSION_KEY, btoa(token)); } catch { /* ignore */ }
}

/** Full logout — wipes every historical key we've ever used so nothing lingers. */
export function clearCustomerToken() {
  safeLocal.rm(PRIMARY_KEY);
  safeSession.rm(LEGACY_SESSION_KEY);
  // Clean up older keys that may still exist on returning devices.
  safeLocal.rm("doozygo_customer");
}

/** Convenience: an object ready to spread into axios/fetch `headers`. */
export function getCustomerAuthHeaders() {
  const token = getCustomerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Synchronous check — just whether a token is present (not whether it's still valid). */
export function isCustomerLoggedIn() {
  return !!getCustomerToken();
}
